import { Scene } from './Scene';
import { ResolvedPrimitive } from '../gltf';
import { GltfMesh } from '../mesh';
import { RenderObject } from '../object/RenderObject';
import { TerrainMaterial } from '../material';
import { VolumeHeightSample } from '../spatial';
import { HeightField, HeightmapHeightField } from './heightfield';
import { buildHolePolygons } from './terrain/TileMeshGenerator';
import { TerrainHoleRef } from './SceneManifest';
import { TileMeshClient } from './terrain/TileMeshClient';
import { TileMeshOutput } from './terrain/TileMeshGenerator';
import { SurfaceRuleSetRef, TerrainSurfaceRegionRef } from './SceneManifest';
import { SurfaceIndexMap } from './terrain/SurfaceIndexMap';
import { SurfaceTextureManager } from './terrain/SurfaceTextureManager';
import {
  TEXELS_PER_METER,
  aabbOverlapsTile,
  changedRegionAabbs,
  rasterizeTile,
  regionOverlapsTile,
} from './terrain/SurfaceRegionRasterizer';

type HeightmapGrid = { data: Uint8Array; width: number; height: number };

/**
 * Self-contained heightmap snapshot — shippable to a worker via structured
 * clone (or transferable) so terrain seeding in the volgrid generator can run
 * off the main thread.
 */
export interface TerrainHeightmapPayload {
  data: Uint8Array;
  width: number;
  height: number;
  amplitude: number;
  worldMinX: number;
  worldMinZ: number;
  worldWidth: number;
  worldDepth: number;
}

type LoadedTile = {
  obj: RenderObject;
  mesh: GltfMesh;
  lod: number;
  /**
   * Surface ids the tile's index map references. Acquired via
   * {@link SurfaceTextureManager.acquire} on load; released on eviction so the
   * manager's refcounts stay honest. Updated when {@link updateSurfaceRegions}
   * re-rasterizes this tile.
   */
  surfaceIds: Set<string>;
  /**
   * Previous-LOD tile fading out during a crossfade. The fade-in tile lives
   * in `obj`/`mesh`; `prev` is destroyed when `tick()` sees the transition
   * complete.
   */
  prev?: { obj: RenderObject; mesh: GltfMesh; lod: number };
  /**
   * Distance-anchored crossfade state. `startDist` is the chebyshev world
   * distance from camera to tile center at the moment the transition began;
   * `sign` is +1 when the new LOD is coarser (camera moving away) or −1 when
   * finer (camera moving closer). Fade progresses with `sign * (d - startDist)`.
   */
  transition?: { startDist: number; sign: 1 | -1 };
};

/**
 * Distance the camera must travel (chebyshev world units) past the LOD-band
 * boundary before the crossfade is fully complete.
 */
const FADE_DISTANCE_METERS = 100;

/**
 * Concentric LOD bands keyed by chebyshev distance from the camera tile.
 *
 *  - LOD0 covers a 9×9 inner ring
 *  - LOD1 extends to a 13×13 ring
 *  - LOD2 extends to a 17×17 ring
 *  - LOD3 extends to a 21×21 ring (≈ 441 tiles total at the largest band)
 */
const LOD_BANDS = [
  { maxDistance: 4, lodLevel: 0 },
  { maxDistance: 6, lodLevel: 1 },
  { maxDistance: 8, lodLevel: 2 },
  { maxDistance: 10, lodLevel: 3 },
] as const;

/**
 * Outermost chebyshev distance any terrain LOD reaches. Beyond this radius the
 * terrain is culled — anything else camera-tracked (scatter, decals, particles)
 * should cap its own ring at the same value to avoid floating-content artifacts
 * over a culled void.
 */
export const TERRAIN_MAX_RING_DISTANCE = LOD_BANDS[LOD_BANDS.length - 1].maxDistance;
const MAX_RING_DISTANCE = TERRAIN_MAX_RING_DISTANCE;

const tileKey = (tx: number, tz: number): string => `${tx},${tz}`;

/**
 * Camera-driven tiled terrain streamer with concentric LOD rings.
 *
 * Tile meshes are generated client-side by a {@link TileMeshClient} (Web Worker)
 * — the streamer no longer fetches per-tile or whole-world bundle GLBs. The
 * shared {@link TerrainMaterial} (PBR textures + slot metadata) is still loaded
 * once at scene activation from the manifest's shared GLB.
 *
 * The LOD ring logic, fade transitions, and eviction are unchanged from the
 * fetch-based predecessor.
 */
export class TiledTerrainStreamer {
  private loaded = new Map<string, LoadedTile>();

  /**
   * Max concurrent worker-side mesh generations during steady-state ring
   * shifts. Matches {@link TiledBvh}'s cap. Initial-load (`loadInitialTiles`)
   * bypasses this — the user is on a loading screen, so worker parallelism
   * is fine there.
   *
   * Per-frame install throttling is intentionally absent: with the scanline
   * rasterizer optimisation, a single install is ~1–3ms, so even all three
   * in-flight workers resolving in the same frame stays under one v-sync.
   * Queueing them would just show as visible terrain pop-in.
   */
  private static readonly MAX_INFLIGHT_LOADS = 3;

  /**
   * Keys currently in the worker pipeline (worker has been asked, no result
   * yet). {@link kickPendingLoads} consults `loading.size` against
   * `MAX_INFLIGHT_LOADS` before starting a new steady-state load. Initial
   * loads add to the set as well but bypass the cap by calling `loadTile`
   * directly (skipping the gate in `kickPendingLoads`).
   */
  private loading = new Set<string>();

  /**
   * Last-computed "what should be loaded right now" map (key → desired LOD),
   * refreshed in `updateCamera` and consumed by `kickPendingLoads` whenever
   * a worker slot frees. Caching it across frames lets the cap defer loads
   * past the one rAF where the camera actually crossed a tile boundary.
   */
  private needed = new Map<string, number>();

  /**
   * Tiles whose `meshClient.generate` rejected this session. `kickPendingLoads`
   * skips these so a deterministically-broken tile (corrupt heightmap chunk,
   * worker exception) can't enter a hot retry loop: every worker slot it
   * frees would otherwise re-fire the same load synchronously, starving
   * every other ring tile. Cleared on `updateCamera` tile-shift (natural
   * "give it another chance" moment) and on `destroyAll`.
   */
  private failed = new Set<string>();

  /**
   * Single-kind regions in priority-descending order. Refreshed when
   * `surfaceRegions` mutates (constructor + `updateSurfaceRegions`); read on
   * every `foot_down` event from {@link getSurfaceAt}, so the sort + filter
   * stay off the per-event hot path.
   */
  private singleRegionsSorted: TerrainSurfaceRegionRef[] = [];

  private tilesX: number;
  private tilesZ: number;
  private tileWorldSize: number;
  private worldMinX: number;
  private worldMinZ: number;
  private worldWidth: number;
  private worldDepth: number;
  private maxLodLevel: number;

  private lastCameraTileX = -999;
  private lastCameraTileZ = -999;
  private lastCameraX = 0;
  private lastCameraZ = 0;

  // Streaming counters. Per-frame work on the main thread is dominated by
  // `loadTile`'s post-await continuation (GltfMesh + SurfaceIndexMap GL
  // creation + rasterizeTile) and tile teardown (`evictTile` + crossfade
  // fade-end). Both happen outside the timed `updateCamera`/`tick` window
  // because the worker resolves asynchronously, so the existing
  // `tickBreakdown.terrainStream` row misses them entirely. Accumulated
  // here between Scene.tick reads and drained via
  // `readAndResetStreamStats()`. `maxInstallCpuMs`/`maxEvictCpuMs` track
  // the worst single continuation in the window (spike signal).
  private streamStats = {
    installs: 0,
    installCpuMs: 0,
    maxInstallCpuMs: 0,
    evicts: 0,
    evictCpuMs: 0,
    maxEvictCpuMs: 0,
  };

  private heightField: HeightField | null = null;

  /**
   * Raw heightmap + bounds — kept so the volgrid worker can sample terrain
   * directly without main-thread roundtrips. Null when no heightmap was
   * supplied (noise-only scenes).
   */
  private heightmapPayload: TerrainHeightmapPayload | null = null;
  /** Sculpt patch as loaded from the manifest. May be `null` if the scene has no sculpt yet. */
  private sculptPatchInitial: HeightmapGrid | null = null;

  constructor(
    private scene: Scene,
    private meshClient: TileMeshClient,
    tileWorldSize: number,
    worldMinX: number,
    worldMinZ: number,
    worldWidth: number,
    worldDepth: number,
    tilesX: number,
    tilesZ: number,
    maxLodLevel: number,
    private surfaceManager: SurfaceTextureManager,
    private sharedMaterial: TerrainMaterial,
    heightmap: HeightmapGrid | null = null,
    heightmapAmplitude: number = 0,
    heightmapCellSize: number = 0,
    private surfaceRegions: TerrainSurfaceRegionRef[] = [],
    private defaultSurfaceId: string | undefined = undefined,
    /**
     * Resolved project-scoped surface rule sets, keyed by id. Consumed when an
     * auto-fill surface region (`kind: 'auto'`) is rasterized. Mutated only
     * through [updateSurfaceRegions].
     */
    private ruleSets: Map<string, SurfaceRuleSetRef> = new Map(),
    /**
     * Optional global default rule set id, evaluated per terrain texel as the
     * tile's base fill anywhere not painted by a higher-priority single-surface
     * polygon. Falls back to [defaultSurfaceId] flat fill when null / unresolvable.
     */
    private defaultRuleSetId: string | undefined = undefined,
    sculptPatch: HeightmapGrid | null = null,
    private sculptAmplitudeInitial: number = 0,
    /**
     * Terrain hole polygons from the manifest. Forwarded to the
     * `TileMeshClient` so every per-tile mesh skips quads inside holes.
     * Editor authoring can replace via [installTerrainHoles].
     */
    private terrainHolesInitial: readonly TerrainHoleRef[] = [],
  ) {
    // Track the sculpt grid post-construction so editor brush tooling can
    // adopt the loaded patch as its working buffer without re-fetching the PNG.
    this.sculptPatchInitial = sculptPatch;
    // Forward the initial hole set into the mesh client so the first
    // `loadTile` call after construction already cuts out holes.
    if (terrainHolesInitial.length > 0) {
      this.meshClient.updateHoles(buildHolePolygons(terrainHolesInitial));
    }
    this.tileWorldSize = tileWorldSize;
    this.worldMinX = worldMinX;
    this.worldMinZ = worldMinZ;
    this.worldWidth = worldWidth;
    this.worldDepth = worldDepth;
    this.tilesX = tilesX;
    this.tilesZ = tilesZ;
    this.maxLodLevel = maxLodLevel;
    this.rebuildSingleRegionsCache();
    if (heightmap) {
      this.heightmapPayload = {
        data: heightmap.data,
        width: heightmap.width,
        height: heightmap.height,
        amplitude: heightmapAmplitude,
        worldMinX,
        worldMinZ,
        worldWidth,
        worldDepth,
      };
      this.heightField = new HeightmapHeightField({
        heightmap,
        amplitude: heightmapAmplitude,
        worldMinX,
        worldMinZ,
        worldWidth,
        worldDepth,
        cellSize: heightmapCellSize,
        tileWorldSize,
        tilesX,
        tilesZ,
        sculptPatch: sculptPatch ?? undefined,
        sculptAmplitude: this.sculptAmplitudeInitial,
      });
    }
  }

  /**
   * Returns the streamer's heightfield (heightmap-driven). Null when the scene
   * has no authored heightmap (noise-only terrain). The asset-manager's
   * `TerrainHeightOracle` consumes this for editor-time height queries; runtime
   * physics in playful uses {@link sampleHeight} directly.
   */
  getHeightField(): HeightField | null {
    return this.heightField;
  }

  /**
   * Resolve which surface covers the world XZ position. Walks the
   * single-kind surface regions in priority order (highest first), returns
   * the first one whose polygon contains the point. Falls back to
   * `defaultSurfaceId` (which itself may be undefined). Auto-fill rule-set
   * regions are skipped in v1 — they need per-texel rule evaluation, which is
   * not yet exposed on the runtime path.
   */
  getSurfaceAt(x: number, z: number): string | null {
    for (const region of this.singleRegionsSorted) {
      // Cache only contains single-kind regions but the runtime union narrows
      // it through; the `as` reassures the compiler we won't read `surfaceId`
      // off an auto entry.
      if (pointInPolygon(x, z, region.vertices)) {
        return (region as { surfaceId: string }).surfaceId;
      }
    }
    return this.defaultSurfaceId ?? null;
  }

  /**
   * Convenience: resolves the surface at (x, z) and returns its
   * `footstepClipId` (or null if no surface, or surface has no clip set).
   * Footstep system in playful uses this on each `foot_down` animation event.
   */
  getFootstepClipAt(x: number, z: number): string | null {
    const surfaceId = this.getSurfaceAt(x, z);
    if (!surfaceId) return null;
    return this.surfaceManager.getSurface(surfaceId)?.footstepClipId ?? null;
  }

  /**
   * Recompute the priority-sorted list of single-kind regions used by
   * {@link getSurfaceAt}. Called only on region-set mutation; the per-event
   * `foot_down` path then iterates without allocating or sorting.
   */
  private rebuildSingleRegionsCache(): void {
    this.singleRegionsSorted = this.surfaceRegions
      .filter((r) => (r.kind ?? 'single') === 'single')
      .slice()
      .sort((a, b) => b.priority - a.priority);
  }

  /**
   * Snapshot of the heightmap for off-main-thread consumers (volgrid worker).
   * Returns null for noise-only scenes. The caller is responsible for cloning
   * the `data` Uint8Array if transferring its buffer to a worker.
   */
  getHeightmapPayload(): TerrainHeightmapPayload | null {
    return this.heightmapPayload;
  }

  /**
   * Sample terrain height at (x, z). When `heightmapCellSize > 0`, snaps to the
   * terrain vertex grid and bilinearly interpolates between the four surrounding
   * vertex heights — matching the piecewise-linear surface the GPU renders exactly.
   * Returns null when no heightmap is available (noise terrain).
   */
  sampleHeight(x: number, z: number): number | null {
    return this.heightField ? this.heightField.sampleHeight(x, z) : null;
  }

  /** Sample height + analytic surface normal via central differences. */
  sampleHeightWithNormal(x: number, z: number): VolumeHeightSample | null {
    if (!this.heightField) return null;
    const sample = this.heightField.sampleHeightWithNormal(x, z);
    if (!sample) return null;
    return { height: sample.height, normal: sample.normal };
  }

  /**
   * Generate the terrain mesh for tile (tx, tz) at LOD 0 — same call the
   * streamer's render path uses. Routed through the (optionally worker-backed)
   * `TileMeshClient`, so callers that need terrain geometry off the main
   * thread automatically benefit when the worker is wired up. Used by the
   * BVH-backed physics path to build terrain BLAS that matches what's drawn.
   */
  generateTerrainMeshForTile(tx: number, tz: number): Promise<TileMeshOutput> {
    const tileMinX = this.worldMinX + tx * this.tileWorldSize;
    const tileMinZ = this.worldMinZ + tz * this.tileWorldSize;
    const tileMaxX = Math.min(tileMinX + this.tileWorldSize, this.worldMinX + this.worldWidth);
    const tileMaxZ = Math.min(tileMinZ + this.tileWorldSize, this.worldMinZ + this.worldDepth);
    return this.meshClient.generate(
      {
        tileX: tx,
        tileZ: tz,
        worldMinX: tileMinX,
        worldMinZ: tileMinZ,
        worldMaxX: tileMaxX,
        worldMaxZ: tileMaxZ,
      },
      0,
    );
  }

  /** The shared surface texture manager — exposed so the editor can `invalidate` on surface edits. */
  getSurfaceManager(): SurfaceTextureManager {
    return this.surfaceManager;
  }

  /**
   * Sculpt patch the streamer was constructed with (as published by the
   * scene manifest). Used by the editor brush tool to seed its working
   * buffer with the existing strokes instead of starting from a flat patch.
   */
  getInitialSculptPatch(): { grid: HeightmapGrid | null; amplitude: number } {
    return { grid: this.sculptPatchInitial, amplitude: this.sculptAmplitudeInitial };
  }

  /**
   * Replace the active set of terrain hole polygons. Subsequent tile mesh
   * generations skip quads inside any hole. Editor authoring calls this
   * each time the user commits a hole edit; runtime is constructed with
   * the manifest's holes and rarely needs to re-call.
   *
   * Does NOT remesh already-loaded tiles — pair with
   * {@link regenerateLoadedTilesInAabb} over the union of old/new hole
   * AABBs.
   */
  installTerrainHoles(holes: readonly TerrainHoleRef[]): void {
    this.meshClient.updateHoles(buildHolePolygons(holes));
  }

  /**
   * Install or replace the sculpt-delta grid on the height sampler driving
   * mesh generation. Subsequent `loadTile` calls will sample heights with the
   * patch composed; mutating the supplied `grid.data` buffer in place — as
   * the editor's brush authoring path does between strokes — is reflected on
   * the very next sample. Pass `null`/zero amplitude to clear the layer.
   *
   * Also updates the streamer's own height field (used by the height oracle
   * for vertex picking) so polygon overlays follow the sculpted surface.
   *
   * Does NOT re-mesh existing tiles; call {@link regenerateLoadedTilesInAabb}
   * afterwards to force the affected tile meshes to re-sample.
   */
  installSculptGrid(grid: HeightmapGrid | null, amplitude: number): void {
    this.meshClient.updateSculpt(grid, amplitude);
    if (this.heightField instanceof HeightmapHeightField) {
      this.heightField.setSculptPatch(grid, amplitude);
    }
  }

  /**
   * Sample the base heightmap (without sculpt composition) at a world XZ.
   * The Terrain mode brush uses this to compute the per-pixel delta required
   * to drive the composed surface toward an absolute target world Y when
   * the `flatten` tool is active. Returns `null` when the terrain isn't
   * heightmap-backed (noise fallback) — `flatten` then no-ops gracefully.
   */
  sampleBaseHeight(x: number, z: number): number | null {
    if (this.heightField instanceof HeightmapHeightField) {
      return this.heightField.sampleBaseHeight(x, z);
    }
    return null;
  }

  /**
   * Re-mesh every loaded tile whose world AABB overlaps `aabb`. Used by the
   * editor's brush authoring path: after a stroke mutates the sculpt patch
   * buffer, the tiles under the brush footprint must regenerate their meshes
   * from the new sampler. Tiles outside the AABB stay untouched.
   *
   * Returns a promise that resolves when all queued regenerations complete.
   */
  async regenerateLoadedTilesInAabb(aabb: {
    minX: number;
    minZ: number;
    maxX: number;
    maxZ: number;
  }): Promise<void> {
    const promises: Promise<void>[] = [];
    for (const [key, loaded] of this.loaded) {
      const comma = key.indexOf(',');
      const tx = Number(key.slice(0, comma));
      const tz = Number(key.slice(comma + 1));
      const tileMinX = this.worldMinX + tx * this.tileWorldSize;
      const tileMinZ = this.worldMinZ + tz * this.tileWorldSize;
      const tileMaxX = Math.min(tileMinX + this.tileWorldSize, this.worldMinX + this.worldWidth);
      const tileMaxZ = Math.min(tileMinZ + this.tileWorldSize, this.worldMinZ + this.worldDepth);
      if (tileMaxX < aabb.minX || tileMinX > aabb.maxX) continue;
      if (tileMaxZ < aabb.minZ || tileMinZ > aabb.maxZ) continue;
      promises.push(this.loadTile(tx, tz, loaded.lod, false));
    }
    await Promise.all(promises);
  }

  /** Block on the inner 3×3 (LOD0) around (x, z) so the player never spawns into empty terrain. */
  async loadInitialTiles(x: number, z: number): Promise<void> {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;
    this.lastCameraX = x;
    this.lastCameraZ = z;

    // Block on the inner 3×3 LOD0 ring so the player never spawns into empty
    // terrain. These fire directly (bypassing the cap) — the user is on a
    // loading screen, so worker parallelism is fine.
    const blockingPromises: Promise<void>[] = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (!this.inRange(ntx, ntz)) continue;
        blockingPromises.push(this.loadTile(ntx, ntz, 0, false));
      }
    }
    await Promise.all(blockingPromises);

    // Background fill: populate `needed` (in cheb-ascending order so close
    // LOD0 tiles beat distant LOD3 corners — Map preserves insertion order)
    // and let `kickPendingLoads` drive the rest under MAX_INFLIGHT_LOADS.
    // Firing all ~430 outer tiles directly here would jam the worker queue
    // with stale spawn-centered work for ~20s, and any subsequent
    // `updateCamera` tile-shift would then be blocked by the cap until the
    // jam drained — manifesting as "LOD0 around the camera doesn't load
    // until you move away and back".
    type Candidate = { key: string; cheb: number; lod: number };
    const candidates: Candidate[] = [];
    for (let dx = -MAX_RING_DISTANCE; dx <= MAX_RING_DISTANCE; dx++) {
      for (let dz = -MAX_RING_DISTANCE; dz <= MAX_RING_DISTANCE; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (!this.inRange(ntx, ntz)) continue;
        const cheb = Math.max(Math.abs(dx), Math.abs(dz));
        const lod = this.lodForDistance(cheb);
        if (lod < 0) continue;
        candidates.push({ key: tileKey(ntx, ntz), cheb, lod });
      }
    }
    candidates.sort((a, b) => a.cheb - b.cheb);
    this.needed.clear();
    for (const c of candidates) this.needed.set(c.key, c.lod);
    this.kickPendingLoads();
  }

  /** Per-frame hook: re-evaluate concentric LOD rings. No-op when the camera tile hasn't changed. */
  updateCamera(x: number, z: number): void {
    this.lastCameraX = x;
    this.lastCameraZ = z;
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);

    if (tx === this.lastCameraTileX && tz === this.lastCameraTileZ) return;
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;

    // Tile-shift is the natural "give it another chance" moment for tiles
    // whose previous load rejected. The intervening movement is enough
    // signal that the user might benefit from a retry (e.g. a flaky fetch
    // succeeds now), and the cap still bounds the retry rate.
    this.failed.clear();

    this.needed.clear();
    for (let dx = -MAX_RING_DISTANCE; dx <= MAX_RING_DISTANCE; dx++) {
      for (let dz = -MAX_RING_DISTANCE; dz <= MAX_RING_DISTANCE; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (!this.inRange(ntx, ntz)) continue;
        const cheb = Math.max(Math.abs(dx), Math.abs(dz));
        const lod = this.lodForDistance(cheb);
        if (lod < 0) continue;
        this.needed.set(tileKey(ntx, ntz), lod);
      }
    }

    for (const [key, entry] of [...this.loaded.entries()]) {
      const want = this.needed.get(key);
      if (want === undefined) {
        this.evictTile(key);
      } else if (want !== entry.lod) {
        // Old LOD stays loaded; loadTile will swap it.
      }
    }

    this.kickPendingLoads();
  }

  /**
   * Start as many worker loads as fit under {@link MAX_INFLIGHT_LOADS}.
   * Called whenever the desired ring changes (`updateCamera`) or a worker
   * slot frees (end of `loadTile`). Idempotent — skips tiles already
   * loading or already at the desired LOD.
   */
  private kickPendingLoads(): void {
    for (const [key, lod] of this.needed) {
      if (this.loading.size >= TiledTerrainStreamer.MAX_INFLIGHT_LOADS) break;
      if (this.loading.has(key)) continue;
      if (this.failed.has(key)) continue;
      const have = this.loaded.get(key);
      if (have && have.lod === lod) continue;
      const [ntxStr, ntzStr] = key.split(',');
      void this.loadTile(Number(ntxStr), Number(ntzStr), lod);
    }
  }

  /**
   * Update surface regions and re-rasterize only the tiles affected by changed
   * regions. Diffs against the previous region list (kept in
   * `this.surfaceRegions`) and skips tiles whose AABB doesn't intersect any
   * changed region — this is the difference between "re-rasterize 80 tiles per
   * drag tick at 60 Hz" and "re-rasterize the 2-3 tiles the polygon actually
   * touches". A change in `defaultSurfaceId` invalidates every tile (the
   * default fills every uncovered texel) and falls back to the full pass.
   */
  public updateSurfaceRegions(
    regions: TerrainSurfaceRegionRef[],
    defaultSurfaceId: string | undefined,
    /**
     * Resolved rule sets used by any auto-fill regions in `regions`. The streamer
     * keeps a copy and consumes it on subsequent (re)rasterizations; omitting
     * this param preserves the existing map (useful for vertex-only edits where
     * only the region polygons changed).
     */
    ruleSets?: Map<string, SurfaceRuleSetRef>,
    /**
     * Set of rule-set ids whose *content* changed since the last update. Auto
     * regions referencing any of these are force-dirtied (so the rasterizer
     * re-runs even though their polygons didn't move). Pass an empty set when
     * only region geometry changed; pass `undefined` when no rule-set tracking
     * is needed (default — same as empty set).
     */
    changedRuleSetIds?: Set<string>,
    /**
     * Optional global default rule set id (or `null` to clear). Omitting the
     * argument preserves the existing value — pass it explicitly when the
     * scene's `defaultRuleSetId` changes.
     */
    defaultRuleSetId?: string | null,
  ): void {
    const previousRegions = this.surfaceRegions;
    const previousDefault = this.defaultSurfaceId;
    const previousDefaultRuleSetId = this.defaultRuleSetId;
    this.surfaceRegions = regions;
    this.defaultSurfaceId = defaultSurfaceId;
    this.rebuildSingleRegionsCache();
    if (ruleSets) this.ruleSets = ruleSets;
    if (defaultRuleSetId !== undefined) {
      this.defaultRuleSetId = defaultRuleSetId ?? undefined;
    }

    const defaultChanged =
      defaultSurfaceId !== previousDefault || this.defaultRuleSetId !== previousDefaultRuleSetId;
    const defaultRsContentChanged =
      this.defaultRuleSetId != null &&
      changedRuleSetIds != null &&
      changedRuleSetIds.has(this.defaultRuleSetId);
    const ruleDirty = changedRuleSetIds && changedRuleSetIds.size > 0;
    // The global default fills every uncovered texel, so any change to it (id
    // swap OR content edit) invalidates every loaded tile.
    const dirtyAabbs =
      defaultChanged || defaultRsContentChanged
        ? null
        : changedRegionAabbs(previousRegions, regions);

    // Force-dirty any auto region whose rule set changed (`regionsEqual` can't
    // detect mutated rule-set content; regions still look identical).
    const extraDirty: ReturnType<typeof changedRegionAabbs> = [];
    if (ruleDirty && dirtyAabbs) {
      for (const r of regions) {
        if (r.kind !== 'auto') continue;
        if (!changedRuleSetIds!.has(r.ruleSetId)) continue;
        let minX = r.vertices[0][0], maxX = minX, minZ = r.vertices[0][1], maxZ = minZ;
        for (const v of r.vertices) {
          if (v[0] < minX) minX = v[0];
          if (v[0] > maxX) maxX = v[0];
          if (v[1] < minZ) minZ = v[1];
          if (v[1] > maxZ) maxZ = v[1];
        }
        extraDirty.push({ minX, maxX, minZ, maxZ });
      }
    }
    const effectiveDirty = dirtyAabbs ? [...dirtyAabbs, ...extraDirty] : null;
    // Nothing changed → nothing to do.
    if (effectiveDirty && effectiveDirty.length === 0) return;

    const gl = this.scene.ctx.gl;
    const layerMap = this.buildLayerMap(regions, defaultSurfaceId);
    const defaultLayer = this.surfaceManager.getLayerIndex(defaultSurfaceId);
    for (const [key, tile] of this.loaded) {
      const [txStr, tzStr] = key.split(',');
      const tx = Number(txStr);
      const tz = Number(tzStr);
      const tileMinX = this.worldMinX + tx * this.tileWorldSize;
      const tileMinZ = this.worldMinZ + tz * this.tileWorldSize;
      const tileMaxX = tileMinX + this.tileWorldSize;
      const tileMaxZ = tileMinZ + this.tileWorldSize;
      const tileBounds = { worldMinX: tileMinX, worldMinZ: tileMinZ, worldMaxX: tileMaxX, worldMaxZ: tileMaxZ };

      // Skip tiles untouched by any changed region (unless the default-fill
      // itself changed, in which case every tile needs a redraw).
      if (effectiveDirty && !effectiveDirty.some((a) => aabbOverlapsTile(a, tileBounds))) continue;

      const relevantRegions = regions.filter((r) => regionOverlapsTile(r, tileBounds));
      const mapData = rasterizeTile(
        tileBounds,
        relevantRegions,
        layerMap,
        defaultLayer,
        TEXELS_PER_METER,
        defaultSurfaceId ?? null,
        this.ruleSets,
        this.heightField,
        this.defaultRuleSetId ?? null,
      );
      // Diff the new surface set against the cached one and acquire/release
      // per the difference; refcounts stay correct without leaking layers.
      this.reconcileSurfaceRefs(tile, mapData.surfaceIds);
      for (const tileObj of [tile.obj, tile.prev?.obj]) {
        if (!tileObj) continue;
        const newMap = new SurfaceIndexMap(gl, mapData);
        this.destroyTileIndexMap(tileObj);
        tileObj.terrainSurfaceIndices = newMap.indicesTex;
        tileObj.terrainSurfaceIndicesTexture = newMap.indicesWrapper;
        tileObj.terrainSurfaceWeight = newMap.weightTex;
        tileObj.terrainSurfaceWeightTexture = newMap.weightWrapper;
        tileObj.terrainIndexMapBounds = [tileMinX, tileMinZ, 1 / (tileMaxX - tileMinX), 1 / (tileMaxZ - tileMinZ)];
      }
    }
  }

  /**
   * Build a surfaceId → layer index map for the rasterizer. Wraps
   * {@link SurfaceTextureManager.getLayerIndex} so unknown surfaces (e.g. ones
   * added mid-session and not yet registered) fall back to layer 0.
   */
  private buildLayerMap(
    regions: TerrainSurfaceRegionRef[],
    defaultSurfaceId: string | undefined,
  ): Map<string, number> {
    const m = new Map<string, number>();
    if (defaultSurfaceId) m.set(defaultSurfaceId, this.surfaceManager.getLayerIndex(defaultSurfaceId));
    // Global default rule set: widen the map to every surface its rules might
    // pick. Same conservative-union policy as auto regions below.
    if (this.defaultRuleSetId) {
      const rs = this.ruleSets.get(this.defaultRuleSetId);
      if (rs) {
        for (const rule of rs.rules) {
          if (rule.surfaceId) m.set(rule.surfaceId, this.surfaceManager.getLayerIndex(rule.surfaceId));
        }
      }
    }
    for (const r of regions) {
      if (r.kind === 'auto') {
        // Auto-fill: register every surface the referenced rule set might pick
        // at any texel inside this region's footprint (conservative union).
        const rs = this.ruleSets.get(r.ruleSetId);
        if (!rs) continue;
        for (const rule of rs.rules) {
          if (rule.surfaceId) m.set(rule.surfaceId, this.surfaceManager.getLayerIndex(rule.surfaceId));
        }
        continue;
      }
      if (!r.surfaceId) continue;
      m.set(r.surfaceId, this.surfaceManager.getLayerIndex(r.surfaceId));
    }
    return m;
  }

  /**
   * Diff the new surfaceId set against [tile.surfaceIds] and acquire/release on
   * the manager so refcounts stay accurate after region edits. Updates the
   * tile's cached set in place.
   */
  private reconcileSurfaceRefs(tile: LoadedTile, nextIds: Set<string>): void {
    for (const id of nextIds) {
      if (!tile.surfaceIds.has(id)) this.surfaceManager.acquire(id);
    }
    for (const id of tile.surfaceIds) {
      if (!nextIds.has(id)) this.surfaceManager.release(id);
    }
    tile.surfaceIds = nextIds;
  }

  /** Per-frame crossfade driver. Also flushes any queued surface-texture mipmap rebuilds. */
  public tick(cameraX: number, cameraZ: number): void {
    this.lastCameraX = cameraX;
    this.lastCameraZ = cameraZ;
    for (const [key, entry] of this.loaded) {
      if (!entry.prev || !entry.transition) continue;
      const fade = this.computeFade(key, entry.transition);
      if (fade >= 1) {
        const teardownStart = performance.now();
        this.scene.objectManager.removeObject(entry.prev.obj);
        this.destroyTileIndexMap(entry.prev.obj);
        entry.prev.mesh.destroy(this.scene.ctx.gl);
        entry.prev = undefined;
        entry.transition = undefined;
        entry.obj.ditherFade = null;
        const teardownMs = performance.now() - teardownStart;
        this.streamStats.evicts++;
        this.streamStats.evictCpuMs += teardownMs;
        if (teardownMs > this.streamStats.maxEvictCpuMs) {
          this.streamStats.maxEvictCpuMs = teardownMs;
        }
      } else {
        entry.prev.obj.ditherFade = fade;
        entry.obj.ditherFade = -fade;
      }
    }
    this.surfaceManager.flushMipmaps();
  }

  private cameraDistanceToTile(tx: number, tz: number): number {
    const cx = this.worldMinX + (tx + 0.5) * this.tileWorldSize;
    const cz = this.worldMinZ + (tz + 0.5) * this.tileWorldSize;
    return Math.max(Math.abs(this.lastCameraX - cx), Math.abs(this.lastCameraZ - cz));
  }

  private computeFade(key: string, transition: { startDist: number; sign: 1 | -1 }): number {
    const [txStr, tzStr] = key.split(',');
    const d = this.cameraDistanceToTile(Number(txStr), Number(tzStr));
    const f = (transition.sign * (d - transition.startDist)) / FADE_DISTANCE_METERS;
    return Math.min(1, Math.max(0, f));
  }

  private inRange(tx: number, tz: number): boolean {
    return tx >= 0 && tx < this.tilesX && tz >= 0 && tz < this.tilesZ;
  }

  private lodForDistance(cheb: number): number {
    for (const band of LOD_BANDS) {
      if (cheb <= band.maxDistance) {
        return Math.min(band.lodLevel, this.maxLodLevel);
      }
    }
    return -1;
  }

  /** Build a `ResolvedPrimitive` from the worker's typed-array output. */
  private buildPrimitive(
    out: TileMeshOutput,
    tx: number,
    tz: number,
    lod: number,
  ): ResolvedPrimitive {
    return {
      attributes: {
        POSITION: { array: out.positions, type: 'VEC3' },
        NORMAL: { array: out.normals, type: 'VEC3' },
        TEXCOORD_0: { array: out.uvs, type: 'VEC2' },
      },
      indices: out.indices,
      extras: {
        _TERRAIN_TILE: true,
        tileX: tx,
        tileZ: tz,
        lodLevel: lod,
      },
    };
  }

  private async loadTile(
    tx: number,
    tz: number,
    lod: number,
    allowTransition = true,
  ): Promise<void> {
    const key = tileKey(tx, tz);
    const clampedLod = Math.min(lod, this.maxLodLevel);

    const tileMinX = this.worldMinX + tx * this.tileWorldSize;
    const tileMinZ = this.worldMinZ + tz * this.tileWorldSize;
    const tileMaxX = Math.min(tileMinX + this.tileWorldSize, this.worldMinX + this.worldWidth);
    const tileMaxZ = Math.min(tileMinZ + this.tileWorldSize, this.worldMinZ + this.worldDepth);

    this.loading.add(key);

    let out: TileMeshOutput;
    try {
      out = await this.meshClient.generate(
        {
          tileX: tx,
          tileZ: tz,
          worldMinX: tileMinX,
          worldMinZ: tileMinZ,
          worldMaxX: tileMaxX,
          worldMaxZ: tileMaxZ,
        },
        clampedLod,
      );
    } catch (e) {
      console.warn(`Failed to generate terrain tile (${tx},${tz}) lod${clampedLod}:`, e);
      this.loading.delete(key);
      // Suppress retry until the camera moves to a new tile — otherwise a
      // deterministically-failing tile would re-fire on every subsequent
      // worker-slot free, starving the ring.
      this.failed.add(key);
      this.kickPendingLoads();
      return;
    }

    this.loading.delete(key);

    // Camera may have moved during the await; drop if this tile no longer wants
    // exactly this LOD.
    if (this.lodForCurrentCamera(tx, tz) !== lod) {
      this.kickPendingLoads();
      return;
    }

    const installStart = performance.now();
    const prim = this.buildPrimitive(out, tx, tz, clampedLod);
    const gltfMesh = new GltfMesh(
      `_terrain_${tx}_${tz}_lod${clampedLod}`,
      this.scene.ctx,
      this.scene,
      prim,
    );
    const obj = this.scene.objectFactory.instantiateMesh(gltfMesh);
    obj.material = this.sharedMaterial;
    // Index map bounds: (worldMinX, worldMinZ, invWidth, invDepth) for the tile.
    obj.terrainIndexMapBounds = [
      tileMinX,
      tileMinZ,
      1 / (tileMaxX - tileMinX),
      1 / (tileMaxZ - tileMinZ),
    ];

    // Rasterize per-tile surface region index map.
    const tileBounds = { worldMinX: tileMinX, worldMinZ: tileMinZ, worldMaxX: tileMaxX, worldMaxZ: tileMaxZ };
    const relevantRegions = this.surfaceRegions.filter((r) => regionOverlapsTile(r, tileBounds));
    const layerMap = this.buildLayerMap(relevantRegions, this.defaultSurfaceId);
    const defaultLayer = this.surfaceManager.getLayerIndex(this.defaultSurfaceId);
    const mapData = rasterizeTile(
      tileBounds,
      relevantRegions,
      layerMap,
      defaultLayer,
      TEXELS_PER_METER,
      this.defaultSurfaceId ?? null,
      this.ruleSets,
      this.heightField,
      this.defaultRuleSetId ?? null,
    );
    const indexMap = new SurfaceIndexMap(this.scene.ctx.gl, mapData);
    obj.terrainSurfaceIndices = indexMap.indicesTex;
    obj.terrainSurfaceIndicesTexture = indexMap.indicesWrapper;
    obj.terrainSurfaceWeight = indexMap.weightTex;
    obj.terrainSurfaceWeightTexture = indexMap.weightWrapper;

    // Acquire surface texture refs so the manager fetches the PNGs (if not
    // already cached) before this tile's first paint.
    for (const sid of mapData.surfaceIds) {
      if (sid.length > 0) this.surfaceManager.acquire(sid);
    }

    this.scene.instanceManager.registerObject(obj);

    this.installLoadedTile(key, obj, gltfMesh, clampedLod, mapData.surfaceIds, allowTransition);

    const installMs = performance.now() - installStart;
    this.streamStats.installs++;
    this.streamStats.installCpuMs += installMs;
    if (installMs > this.streamStats.maxInstallCpuMs) {
      this.streamStats.maxInstallCpuMs = installMs;
    }

    // Worker slot freed and tile installed — see if any deferred ring entries
    // can start now.
    this.kickPendingLoads();
  }

  private installLoadedTile(
    key: string,
    obj: RenderObject,
    gltfMesh: GltfMesh,
    lod: number,
    surfaceIds: Set<string>,
    allowTransition: boolean,
  ): void {
    const existing = this.loaded.get(key);
    const crossfadeOn = this.scene.ctx.engine.graphicsSettings?.lodCrossfadeEnabled ?? false;
    const useTransition = allowTransition && crossfadeOn && !!existing;

    if (!existing) {
      this.loaded.set(key, { obj, mesh: gltfMesh, lod, surfaceIds });
      return;
    }

    if (!useTransition) {
      this.releaseTileSurfaces(existing);
      this.scene.objectManager.removeObject(existing.obj);
      this.destroyTileIndexMap(existing.obj);
      existing.mesh.destroy(this.scene.ctx.gl);
      if (existing.prev) {
        this.scene.objectManager.removeObject(existing.prev.obj);
        this.destroyTileIndexMap(existing.prev.obj);
        existing.prev.mesh.destroy(this.scene.ctx.gl);
      }
      this.loaded.set(key, { obj, mesh: gltfMesh, lod, surfaceIds });
      return;
    }

    if (existing.prev && existing.prev.lod === lod) {
      // Camera reversed mid-fade and re-requested the same LOD that's still
      // fading out: cancel the transition, restore prev as the active tile.
      // The new `obj` carries its own freshly-acquired surface refs that
      // never made it onto the screen — release them now since we're
      // discarding `obj`.
      this.releaseSurfaceIds(surfaceIds);
      this.scene.objectManager.removeObject(existing.obj);
      this.destroyTileIndexMap(existing.obj);
      existing.mesh.destroy(this.scene.ctx.gl);
      existing.prev.obj.castsShadows = true;
      existing.prev.obj.ditherFade = null;
      this.loaded.set(key, {
        obj: existing.prev.obj,
        mesh: existing.prev.mesh,
        lod: existing.prev.lod,
        // existing.surfaceIds was acquired alongside existing.obj which we
        // also discard. The prev tile's refs live in existing.surfaceIds
        // — they stay valid here.
        surfaceIds: existing.surfaceIds,
      });
      this.scene.objectManager.removeObject(obj);
      this.destroyTileIndexMap(obj);
      gltfMesh.destroy(this.scene.ctx.gl);
      return;
    }
    if (existing.prev) {
      this.scene.objectManager.removeObject(existing.prev.obj);
      this.destroyTileIndexMap(existing.prev.obj);
      existing.prev.mesh.destroy(this.scene.ctx.gl);
    }
    // The existing tile is fading out → release its surface refs once the
    // visual crossfade completes. For simplicity we release now: refcounts
    // are non-negative integers and the manager keeps fetched layers cached
    // at zero, so a brief drop is harmless and avoids tracking refs across
    // the asynchronous fade.
    this.releaseTileSurfaces(existing);
    existing.obj.ditherFade = null;
    existing.obj.castsShadows = false;
    const newPrev = { obj: existing.obj, mesh: existing.mesh, lod: existing.lod };
    const [txStr, tzStr] = key.split(',');
    const startDist = this.cameraDistanceToTile(Number(txStr), Number(tzStr));
    const transition: { startDist: number; sign: 1 | -1 } = {
      startDist,
      sign: lod > existing.lod ? 1 : -1,
    };
    const entry: LoadedTile = { obj, mesh: gltfMesh, lod, surfaceIds, prev: newPrev, transition };
    obj.ditherFade = 0;
    newPrev.obj.ditherFade = 0;
    this.loaded.set(key, entry);
  }

  private releaseTileSurfaces(tile: LoadedTile): void {
    this.releaseSurfaceIds(tile.surfaceIds);
  }

  private releaseSurfaceIds(ids: Set<string>): void {
    for (const sid of ids) {
      if (sid.length > 0) this.surfaceManager.release(sid);
    }
  }

  private lodForCurrentCamera(tx: number, tz: number): number {
    const dx = tx - this.lastCameraTileX;
    const dz = tz - this.lastCameraTileZ;
    return this.lodForDistance(Math.max(Math.abs(dx), Math.abs(dz)));
  }

  /**
   * Tear down every loaded tile, the shared material, the surface manager, and
   * the mesh-gen worker. The streamer is unusable afterward; callers must drop
   * their reference.
   */
  destroyAll(): void {
    for (const key of [...this.loaded.keys()]) this.evictTile(key);
    this.needed.clear();
    this.failed.clear();
    this.loading.clear();
    this.meshClient.destroy();
    this.sharedMaterial.destroy(this.scene.ctx);
    this.surfaceManager.destroy();
  }

  /**
   * Drain the streaming counters accumulated since the previous read. Called
   * once per frame from {@link Scene} to populate the perf overlay.
   */
  readAndResetStreamStats(): {
    installs: number;
    installCpuMs: number;
    maxInstallCpuMs: number;
    evicts: number;
    evictCpuMs: number;
    maxEvictCpuMs: number;
  } {
    const out = { ...this.streamStats };
    this.streamStats.installs = 0;
    this.streamStats.installCpuMs = 0;
    this.streamStats.maxInstallCpuMs = 0;
    this.streamStats.evicts = 0;
    this.streamStats.evictCpuMs = 0;
    this.streamStats.maxEvictCpuMs = 0;
    return out;
  }

  private evictTile(key: string): void {
    const tile = this.loaded.get(key);
    if (!tile) return;
    const evictStart = performance.now();
    this.loaded.delete(key);
    this.releaseTileSurfaces(tile);
    this.scene.objectManager.removeObject(tile.obj);
    this.destroyTileIndexMap(tile.obj);
    tile.mesh.destroy(this.scene.ctx.gl);
    if (tile.prev) {
      this.scene.objectManager.removeObject(tile.prev.obj);
      this.destroyTileIndexMap(tile.prev.obj);
      tile.prev.mesh.destroy(this.scene.ctx.gl);
    }
    const evictMs = performance.now() - evictStart;
    this.streamStats.evicts++;
    this.streamStats.evictCpuMs += evictMs;
    if (evictMs > this.streamStats.maxEvictCpuMs) {
      this.streamStats.maxEvictCpuMs = evictMs;
    }
  }

  private destroyTileIndexMap(obj: RenderObject): void {
    const gl = this.scene.ctx.gl;
    if (obj.terrainSurfaceIndices) {
      gl.deleteTexture(obj.terrainSurfaceIndices);
      obj.terrainSurfaceIndices = null;
      obj.terrainSurfaceIndicesTexture = null;
    }
    if (obj.terrainSurfaceWeight) {
      gl.deleteTexture(obj.terrainSurfaceWeight);
      obj.terrainSurfaceWeight = null;
      obj.terrainSurfaceWeightTexture = null;
    }
  }
}

/**
 * Standard ray-cast point-in-polygon (even-odd rule). Polygon is a closed ring of
 * `[x, z]` vertices in world space; the first vertex is NOT repeated as the last.
 */
function pointInPolygon(x: number, z: number, vertices: readonly number[][]): boolean {
  let inside = false;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    const [xi, zi] = vertices[i];
    const [xj, zj] = vertices[j];
    const intersects = zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}


